/**
 * Farm (农场) - Agricultural building with crops
 * Features: Green curved roof, wooden structure, crop plots, stone wall
 */

import * as THREE from 'three';
import { createCurvedRoof } from '../BuildingMaterials';

export function createFarmMesh(
  group: THREE.Group,
  mainColor: THREE.Color
): void {
  const stoneMat = new THREE.MeshStandardMaterial({ color: 0x6a6a6a, roughness: 0.95 });
  const woodMat = new THREE.MeshStandardMaterial({ color: 0x5d4037, roughness: 0.85 });
  const darkWoodMat = new THREE.MeshStandardMaterial({ color: 0x3d2817, roughness: 0.8 });
  const greenRoofMat = new THREE.MeshStandardMaterial({ color: 0x4a7c3c, roughness: 0.7 });
  const greenEdgeMat = new THREE.MeshStandardMaterial({ color: 0x2e5a2a, roughness: 0.6 });
  const dirtMat = new THREE.MeshStandardMaterial({ color: 0x5a4a3a, roughness: 1.0 });
  const cropMat = new THREE.MeshStandardMaterial({ color: 0x7cb342, roughness: 0.9 });
  const darkGreenMat = new THREE.MeshStandardMaterial({ color: 0x558b2f, roughness: 0.9 });
  const leafMat = new THREE.MeshStandardMaterial({ color: 0x66bb6a, roughness: 0.85 });

  // === Stone Base Platform ===
  const baseGeom = new THREE.BoxGeometry(2.8, 0.2, 2.4);
  const base = new THREE.Mesh(baseGeom, stoneMat);
  base.position.y = 0.1;
  base.receiveShadow = true;
  base.castShadow = true;
  group.add(base);

  // === Stone Wall (Partial) ===
  const stoneWallGeom = new THREE.BoxGeometry(0.8, 0.5, 0.15);
  const stoneWall = new THREE.Mesh(stoneWallGeom, stoneMat);
  stoneWall.position.set(1.15, 0.45, -0.5);
  stoneWall.castShadow = true;
  group.add(stoneWall);

  // Stone blocks on wall
  const blockGeom = new THREE.BoxGeometry(0.25, 0.18, 0.08);
  const blockPositions = [
    { x: 0.95, y: 0.3 }, { x: 1.2, y: 0.3 }, { x: 1.35, y: 0.3 },
    { x: 1.05, y: 0.5 }, { x: 1.3, y: 0.5 },
    { x: 1.15, y: 0.7 }
  ];
  for (const pos of blockPositions) {
    const block = new THREE.Mesh(blockGeom, new THREE.MeshStandardMaterial({ color: 0x7a7a7a, roughness: 0.9 }));
    block.position.set(pos.x, pos.y, -0.42);
    group.add(block);
  }

  // === Main Building Structure ===
  // Back wall
  const backWallGeom = new THREE.BoxGeometry(1.5, 0.9, 0.12);
  const backWall = new THREE.Mesh(backWallGeom, darkWoodMat);
  backWall.position.set(-0.4, 0.7, -0.85);
  backWall.castShadow = true;
  group.add(backWall);

  // Side walls
  const sideWallGeom = new THREE.BoxGeometry(0.12, 0.9, 1.4);
  const leftWall = new THREE.Mesh(sideWallGeom, darkWoodMat);
  leftWall.position.set(-1.1, 0.7, -0.2);
  leftWall.castShadow = true;
  group.add(leftWall);

  const rightWall = new THREE.Mesh(sideWallGeom.clone(), darkWoodMat);
  rightWall.position.set(0.3, 0.7, -0.2);
  rightWall.castShadow = true;
  group.add(rightWall);

  // Front wall sections
  const frontWallSideGeom = new THREE.BoxGeometry(0.4, 0.9, 0.12);
  const frontWallLeft = new THREE.Mesh(frontWallSideGeom, darkWoodMat);
  frontWallLeft.position.set(-0.9, 0.7, 0.45);
  frontWallLeft.castShadow = true;
  group.add(frontWallLeft);

  const frontWallRight = new THREE.Mesh(frontWallSideGeom.clone(), darkWoodMat);
  frontWallRight.position.set(0.1, 0.7, 0.45);
  frontWallRight.castShadow = true;
  group.add(frontWallRight);

  // Door frame
  const doorFrameTopGeom = new THREE.BoxGeometry(0.55, 0.15, 0.12);
  const doorFrameTop = new THREE.Mesh(doorFrameTopGeom, woodMat);
  doorFrameTop.position.set(-0.4, 1.07, 0.45);
  group.add(doorFrameTop);

  // === Support Pillars ===
  const pillarGeom = new THREE.BoxGeometry(0.1, 0.95, 0.1);
  const pillarPositions = [
    { x: -1.05, z: 0.5 },
    { x: 0.25, z: 0.5 },
  ];
  for (const pos of pillarPositions) {
    const pillar = new THREE.Mesh(pillarGeom, woodMat);
    pillar.position.set(pos.x, 0.72, pos.z);
    pillar.castShadow = true;
    group.add(pillar);
  }

  // === Green Curved Roof ===
  // Custom roof for farm (shifted position)
  const roofBaseGeom = new THREE.BoxGeometry(1.8, 0.12, 1.8);
  const roofBase = new THREE.Mesh(roofBaseGeom, greenRoofMat);
  roofBase.position.set(-0.4, 1.25, -0.15);
  roofBase.castShadow = true;
  group.add(roofBase);

  const roofConeGeom = new THREE.ConeGeometry(1.1, 0.65, 4);
  const roofCone = new THREE.Mesh(roofConeGeom, greenRoofMat);
  roofCone.position.set(-0.4, 1.58, -0.15);
  roofCone.rotation.y = Math.PI / 4;
  roofCone.castShadow = true;
  group.add(roofCone);

  // Roof edge
  const roofEdgeGeom = new THREE.BoxGeometry(1.95, 0.06, 1.95);
  const roofEdge = new THREE.Mesh(roofEdgeGeom, greenEdgeMat);
  roofEdge.position.set(-0.4, 1.2, -0.15);
  group.add(roofEdge);

  // === Crop Plots ===
  // Main crop area (right side)
  const cropAreaGeom = new THREE.BoxGeometry(1.2, 0.1, 1.4);
  const cropArea = new THREE.Mesh(cropAreaGeom, dirtMat);
  cropArea.position.set(0.95, 0.25, 0.4);
  cropArea.receiveShadow = true;
  group.add(cropArea);

  // Crop rows
  for (let row = 0; row < 5; row++) {
    for (let col = 0; col < 4; col++) {
      // Determine crop type
      const isTallCrop = (row + col) % 3 === 0;
      const cropType = (row + col) % 2;

      if (isTallCrop) {
        // Tall leafy plant
        const stemGeom = new THREE.CylinderGeometry(0.02, 0.025, 0.25, 5);
        const stem = new THREE.Mesh(stemGeom, darkGreenMat);
        stem.position.set(0.5 + col * 0.25, 0.42, 0.05 + row * 0.25);
        group.add(stem);

        // Leaves
        const leafGeom = new THREE.SphereGeometry(0.08, 6, 4);
        leafGeom.scale(1.2, 0.6, 1.0);
        const leaf = new THREE.Mesh(leafGeom, leafMat);
        leaf.position.set(0.5 + col * 0.25, 0.52, 0.05 + row * 0.25);
        group.add(leaf);
      } else if (cropType === 0) {
        // Short crop
        const cropGeom = new THREE.ConeGeometry(0.04, 0.15, 5);
        const crop = new THREE.Mesh(cropGeom, cropMat);
        crop.position.set(0.5 + col * 0.25, 0.37, 0.05 + row * 0.25);
        group.add(crop);
      } else {
        // Medium crop
        const cropGeom = new THREE.ConeGeometry(0.045, 0.2, 5);
        const crop = new THREE.Mesh(cropGeom, darkGreenMat);
        crop.position.set(0.5 + col * 0.25, 0.4, 0.05 + row * 0.25);
        group.add(crop);
      }
    }
  }

  // === Vine/Climbing Plants on Building ===
  const vineMat = new THREE.MeshStandardMaterial({ color: 0x4caf50, roughness: 0.9 });
  const vineLeafGeom = new THREE.SphereGeometry(0.06, 5, 4);
  vineLeafGeom.scale(1.0, 0.6, 0.8);

  const vinePositions = [
    { x: 0.35, y: 0.5, z: 0.35 },
    { x: 0.35, y: 0.75, z: 0.32 },
    { x: 0.35, y: 0.95, z: 0.38 },
    { x: 0.35, y: 1.1, z: 0.3 },
  ];
  for (const pos of vinePositions) {
    const vine = new THREE.Mesh(vineLeafGeom, vineMat);
    vine.position.set(pos.x, pos.y, pos.z);
    vine.rotation.set(Math.random() * 0.5, Math.random() * 0.5, Math.random() * 0.5);
    group.add(vine);
  }

  // === Potted Plant ===
  const potGeom = new THREE.CylinderGeometry(0.08, 0.1, 0.12, 8);
  const potMat = new THREE.MeshStandardMaterial({ color: 0x8b5a2b, roughness: 0.8 });
  const pot = new THREE.Mesh(potGeom, potMat);
  pot.position.set(-0.85, 0.31, 0.7);
  group.add(pot);

  const plantGeom = new THREE.SphereGeometry(0.1, 6, 5);
  plantGeom.scale(1.0, 0.7, 1.0);
  const plant = new THREE.Mesh(plantGeom, leafMat);
  plant.position.set(-0.85, 0.45, 0.7);
  group.add(plant);

  // === Wooden Crates ===
  const crateGeom = new THREE.BoxGeometry(0.22, 0.18, 0.22);
  const crateMat = new THREE.MeshStandardMaterial({ color: 0x6d5c4a, roughness: 0.85 });

  const crate1 = new THREE.Mesh(crateGeom, crateMat);
  crate1.position.set(0.5, 0.29, -0.6);
  group.add(crate1);

  const crate2 = new THREE.Mesh(crateGeom.clone(), crateMat);
  crate2.position.set(0.5, 0.47, -0.6);
  crate2.rotation.y = 0.3;
  group.add(crate2);

  // Vegetables in crate
  const vegMat = new THREE.MeshStandardMaterial({ color: 0xff7043, roughness: 0.8 });
  for (let i = 0; i < 4; i++) {
    const vegGeom = new THREE.SphereGeometry(0.04, 5, 4);
    const veg = new THREE.Mesh(vegGeom, vegMat);
    veg.position.set(
      0.45 + (i % 2) * 0.08,
      0.58,
      -0.62 + Math.floor(i / 2) * 0.06
    );
    group.add(veg);
  }

  // === Scarecrow ===
  // Post
  const scarecrowPostGeom = new THREE.CylinderGeometry(0.03, 0.035, 0.9, 6);
  const scarecrowPost = new THREE.Mesh(scarecrowPostGeom, woodMat);
  scarecrowPost.position.set(0.75, 0.65, 0.95);
  group.add(scarecrowPost);

  // Arms
  const armGeom = new THREE.BoxGeometry(0.5, 0.04, 0.04);
  const arm = new THREE.Mesh(armGeom, woodMat);
  arm.position.set(0.75, 0.9, 0.95);
  group.add(arm);

  // Head (bundle)
  const headGeom = new THREE.SphereGeometry(0.08, 6, 5);
  const headMat = new THREE.MeshStandardMaterial({ color: 0xc9a66b, roughness: 0.9 });
  const head = new THREE.Mesh(headGeom, headMat);
  head.position.set(0.75, 1.05, 0.95);
  group.add(head);

  // === Watering Can ===
  const canBodyGeom = new THREE.CylinderGeometry(0.06, 0.08, 0.12, 8);
  const canMat = new THREE.MeshStandardMaterial({ color: 0x607d8b, roughness: 0.6 });
  const canBody = new THREE.Mesh(canBodyGeom, canMat);
  canBody.position.set(-0.5, 0.31, 0.65);
  group.add(canBody);

  const spoutGeom = new THREE.CylinderGeometry(0.015, 0.025, 0.12, 6);
  const spout = new THREE.Mesh(spoutGeom, canMat);
  spout.position.set(-0.42, 0.35, 0.65);
  spout.rotation.z = Math.PI / 4;
  group.add(spout);
}
